import { useCallback, useEffect, useRef, useState } from "react";
import { Select, Spin } from "antd";
import { connect, mapReadPretty } from "@formily/react";
import { PreviewText } from "@formily/antd-v5";
import { useTranslation } from "react-i18next";
import { ApiService } from "../api";

type Option = { value: string; label: string };

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Type-to-search picker over application users, for schema fields that name a
 * person (e.g. the PTW gas-test "Analysed by").
 *
 * The stored value is the user's **display name**, not their id: the name is
 * what the printed permit and the request list columns need, and it keeps the
 * field backward-compatible with the values written while it was a plain Input.
 */
const UserSearchSelect = ({
  value,
  onChange,
  placeholder,
  disabled,
  style,
}: any) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<Option[]>([]);
  const [fetching, setFetching] = useState(false);
  // Guards against out-of-order responses: only the newest search may render.
  const requestSeq = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const search = useCallback((term: string) => {
    const seq = ++requestSeq.current;
    setFetching(true);
    ApiService.apiAccountsUsersList(term || undefined)
      .then((users) => {
        if (seq !== requestSeq.current) return;
        setOptions(
          (users ?? [])
            .filter((u) => !!u.name)
            .map((u) => ({ value: u.name, label: u.name }))
        );
      })
      .catch(() => {
        if (seq === requestSeq.current) setOptions([]);
      })
      .finally(() => {
        if (seq === requestSeq.current) setFetching(false);
      });
  }, []);

  const handleSearch = useCallback(
    (term: string) => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => search(term), SEARCH_DEBOUNCE_MS);
    },
    [search]
  );

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // A saved value (or one typed before this field became a select) won't be in
  // the freshly-fetched option list, which would render the box blank. Keep it
  // as an option so the field always shows what is actually stored.
  const merged = value && !options.some((o) => o.value === value)
    ? [{ value, label: value }, ...options]
    : options;

  return (
    <Select
      showSearch
      allowClear
      value={value || undefined}
      disabled={disabled}
      style={{ width: "100%", ...(style ?? {}) }}
      placeholder={placeholder ?? t("Type name..")}
      // The API already filters; don't filter the results again client-side.
      filterOption={false}
      onSearch={handleSearch}
      onFocus={() => {
        if (options.length === 0) search("");
      }}
      onChange={(next) => onChange?.(next ?? "")}
      notFoundContent={fetching ? <Spin size="small" /> : null}
      options={merged}
    />
  );
};

/**
 * `mapReadPretty` gives the field the same read-only rendering as a plain
 * Input, so approved/closed permits show the name as text rather than a live,
 * clickable dropdown.
 */
const UserSelect = connect(
  UserSearchSelect,
  mapReadPretty(PreviewText.Input)
);

export default UserSelect;
